import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Checkbox,
  Button,
  Divider,
  Typography,
  Space,
  Input,
  message,
} from "antd";
import { UserService } from "../sdk";

const { Title, Text } = Typography;

type PermItem = {
  key: string; // a chave (importante)
  label: string; // o "botão" / label (apenas visual)
};

type PermSection = {
  title: string;
  items: PermItem[];
};

// ✅ Edite aqui conforme sua tabela
const SECTIONS: PermSection[] = [
    {
    title: "Início",
    items: [
      { key: "in.grafico", label: "Gráficos" },
    ],
  },
  {
    title: "Venda",
    items: [
      { key: "ve.vendas", label: "Vendas" },
      { key: "ve.orcamentos", label: "Orçamentos" },
      { key: "ve.cancelamento", label: "Cancelamento" },
      { key: "ve.devolucao", label: "Devolução" },
      { key: "ve.reimpressao", label: "Reimpressão" },
      { key: "ve.log", label: "Log" },
    ],
  },
  {
    title: "Operações",
    items: [
      { key: "op.nfe", label: "Nota Fiscal NFE DANFE" },
      { key: "op.cadprod", label: "Cadastro de Produtos" },
      { key: "op.cadcli", label: "Cadastro de Clientes" },
      { key: "op.envnotas", label: "Envio de Notas" },
      { key: "op.relarios", label: "Relatórios" },
      { key: "op.estoque", label: "Estoque" },
    ],
  },
  {
    title: "Operações > Cadastro de Produtos",
    items: [
      { key: "op.cadp.antigo", label: "Gerenciar Produtos (Antigo)" },
      { key: "op.cadp.exportar", label: "Exportar Produtos" },
      { key: "op.cadp.importar", label: "Importar Produtos" },
      { key: "op.cadp.novo", label: "Gerenciar Produtos (Novo)" },
      { key: "op.cadp.gerenciarcategorias", label: "Gerenciar Categorias" },
    ],
  },
  {
    title: "Operações > Relatórios",
    items: [
      { key: "op.rel.vendasporcliente", label: "Vendas por Cliente" },
      { key: "op.rel.vendasporproduto", label: "Vendas por Produto" },
      { key: "op.rel.vendasporvendedor", label: "Vendas por Vendedor" },
      { key: "op.rel.controledecaixa", label: "Controle de Caixa" },
      { key: "op.rel.controledevendas", label: "Controle de Vendas" },
      { key: "op.rel.produtoscomestoque", label: "Produtos c/ Estoque" },
    ],
  },
  {
    title: "Operações > Estoque",
    items: [
      { key: "op.est.estoque", label: "Estoque" },
      { key: "op.est.xml", label: "Entrada XML" },
      { key: "op.est.manual", label: "Entrada Manual" },
    ],
  },
    {
    title: "Automação",
    items: [
      { key: "pc.aut", label: "Automação" },
    ],
  },
];

export default function ModalBloqueiosUsuario(props: {
  onSuccess: () => any;
  usuarioID: string;
}): JSX.Element {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [resultado, setResultado] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  // Mantém a ordem da tela (importante pra gerar string estável)
  const allItemsOrdered = useMemo<PermItem[]>(() => SECTIONS.flatMap((s) => s.items), []);

  // Set com as chaves válidas (pra ignorar chaves antigas/inválidas vindas do backend)
  const allowedKeys = useMemo<Set<string>>(
    () => new Set(allItemsOrdered.map((i) => i.key)),
    [allItemsOrdered]
  );

  const isChecked = (k: string): boolean => selected.has(k);

  const toggleKey = (k: string, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(k);
      else next.delete(k);
      return next;
    });
  };

  const sectionKeys = (section: PermSection): string[] => section.items.map((i) => i.key);

  const isSectionAllChecked = (section: PermSection): boolean => {
    const keys = sectionKeys(section);
    return keys.length > 0 && keys.every((k) => selected.has(k));
  };

  const toggleSectionAll = (section: PermSection, checked: boolean): void => {
    const keys = sectionKeys(section);
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (checked ? next.add(k) : next.delete(k)));
      return next;
    });
  };

  const selecionarTudo = (): void => {
    setSelected(new Set(allItemsOrdered.map((i) => i.key)));
  };

  const limparTudo = (): void => {
    setSelected(new Set());
    setResultado("");
  };

  // ✅ Carrega os bloqueios ao abrir / ao mudar usuarioID e já marca os checkboxes
  useEffect(() => {
    let alive = true;

    const carregar = async () => {
      if (!props.usuarioID) return;

      try {
        setLoading(true);

        // ✅ você precisa ter esse método no UserService:
        // UserService.getBloqueiosDoUsuario(usuarioID)
        const resp: any = await UserService.getBloqueiosDoUsuario(props.usuarioID);

        // resp pode ser string pura OU { bloqueiosDoUsuario: "..." }
        const rawStr =
          typeof resp === "string" ? resp : (resp?.bloqueiosDoUsuario ?? "");

        const str = String(rawStr ?? "").trim();

        // parse: "a,b,c" -> ["a","b","c"]
        const keys = str
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        // só marca o que existe na tela
        const nextSelected = new Set(keys.filter((k) => allowedKeys.has(k)));

        if (!alive) return;

        setSelected(nextSelected);

        // normaliza a string na ordem da tela (igual seu salvar)
        const normalized = allItemsOrdered
          .map((i) => i.key)
          .filter((k) => nextSelected.has(k))
          .join(",");

        setResultado(normalized);
      } catch (err: any) {
        // Se não existir bloqueio ainda, pode dar 404 — nesse caso só deixa vazio
        const status = err?.response?.status;
        if (status && status !== 404) {
          console.log(err);
          message.error("Não consegui carregar os bloqueios do usuário.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    carregar();

    return () => {
      alive = false;
    };
  }, [props.usuarioID, allowedKeys, allItemsOrdered]);

  const salvar = async () => {
    const str = allItemsOrdered
      .map((i) => i.key)
      .filter((k) => selected.has(k))
      .join(",");

    setResultado(str);

    try {
      setLoading(true);

      // ⚠️ Para ter status, seu inserirBloqueiosDoUsuario precisa retornar o AxiosResponse
      // (não pode terminar com .then(this.getData))
      const resp: any = await UserService.inserirBloqueiosDoUsuario(str, props.usuarioID);

      const status = resp?.status; // 200/204 etc
      if (status === 204 || status === 200) {
        message.success("Salvo com sucesso!");
        props.onSuccess();
        return;
      }

      // fallback (caso backend retorne algo diferente)
      message.success("Salvo!");
      props.onSuccess();
    } catch (err) {
      console.log(err);
      message.error("Não consegui salvar os bloqueios.");
    } finally {
      setLoading(false);
    }
  };

  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(resultado);
      message.success("Copiado!");
    } catch {
      message.error("Não consegui copiar. Copie manualmente do campo.");
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 960, margin: "0 auto" }}>
      <Title level={3} style={{ marginTop: 0 }}>
        Permissões por chave
      </Title>

      <Card style={{ marginBottom: 12 }} loading={loading}>
        <Space wrap>
          <Button type="primary" onClick={salvar} loading={loading}>
            Salvar (gerar string)
          </Button>
          <Button onClick={selecionarTudo} disabled={loading}>
            Selecionar tudo
          </Button>
          <Button danger onClick={limparTudo} disabled={loading}>
            Limpar
          </Button>
        </Space>

        <Divider />

        <Text strong>Resultado:</Text>
        <Input.TextArea
          value={resultado}
          readOnly
          placeholder="Clique em “Salvar” para gerar a string…"
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{ marginTop: 8 }}
        />

        <div style={{ marginTop: 8 }}>
          <Button onClick={copiar} disabled={!resultado || loading}>
            Copiar
          </Button>
        </div>
      </Card>

      {SECTIONS.map((section) => (
        <Card key={section.title} style={{ marginBottom: 12 }} loading={loading}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              {section.title}
            </Title>

            <Checkbox
              checked={isSectionAllChecked(section)}
              onChange={(e) => toggleSectionAll(section, e.target.checked)}
              disabled={loading}
            >
              Marcar todos
            </Checkbox>
          </div>

          <Divider style={{ margin: "12px 0" }} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            {section.items.map((item) => (
              <div
                key={item.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "8px 10px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 8,
                  opacity: loading ? 0.8 : 1,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Text strong>{item.label}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {item.key}
                  </Text>
                </div>

                <Checkbox
                  checked={isChecked(item.key)}
                  onChange={(e) => toggleKey(item.key, e.target.checked)}
                  disabled={loading}
                />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
